import React, { Fragment, memo, useCallback, useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"
import { Handle, Position, useReactFlow, useStoreApi } from "reactflow"

import { Box, FormControl, Grid, MenuItem, Select } from "@mui/material"
import { Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { getTheme } from "../../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../../services/KonanAPI"
import type { PaginatedCalculatorListList } from "../../../../../types/generated/api/PaginatedCalculatorListList"
import type { PaginatedDeploymentListList } from "../../../../../types/generated/api/PaginatedDeploymentListList"
import type { PaginatedProgramList } from "../../../../../types/generated/api/PaginatedProgramList"
import type { PaginatedRuleSetListList } from "../../../../../types/generated/api/PaginatedRuleSetListList"
import type { PaginatedScoreCardSetListList } from "../../../../../types/generated/api/PaginatedScoreCardSetListList"
import type { PaginatedScriptGroupListList } from "../../../../../types/generated/api/PaginatedScriptGroupListList"
import type { PaginatedTagListListList } from "../../../../../types/generated/api/PaginatedTagListListList"
import { RuleSetCreateRequest } from "../../../../../types/generated/api/RuleSetCreateRequest"
import { getEnvVar, isEnvVariableTruthy } from "../../../../../utils/genericHelpers"
import type { UpdateGraphParams } from "../../../types/interfaces"
import { isNodeInFilter, isNodeInLoop, preDefinedRoutesForNodes } from "../../../utils/workflowHelpers"
import { CalculatorNode } from "../CalculatorNode"
import { FilterNode } from "../FilterNode"
import { LoopStartNode } from "../LoopStartNode"
import { ProgramNode } from "../ProgramNode"
import { ProjectNode } from "../ProjectNode"
import { RulesetNode } from "../RulesetNode"
import { ScorecardsetNode } from "../ScorecardsetNode"
import { ScriptNode } from "../ScriptNode"
import { TaglistNode } from "../TaglistNode"
import { LabeledNode } from "./LabeledNode"

import styles from "../Nodes.module.scss"

type MenuItemsWithTypesProps = {
  Item: React.ReactElement
  tooltipText: string
  tooltipCondition: boolean
  onSelectingBlock: () => void
  isNodeDisabled?: boolean
}

export function MenuItemWithTooltip(props: Readonly<MenuItemsWithTypesProps>): React.ReactElement {
  const { Item, tooltipCondition, tooltipText, onSelectingBlock, isNodeDisabled = false } = props
  return (
    <div>
      {tooltipCondition ? (
        <Tooltip title={tooltipText} placement="top">
          <span style={{ pointerEvents: "none" }}>
            <MenuItem onClick={() => onSelectingBlock()} disabled={true}>
              {Item}
            </MenuItem>
          </span>
        </Tooltip>
      ) : (
        <MenuItem onClick={() => onSelectingBlock()} disabled={isNodeDisabled}>
          {Item}
        </MenuItem>
      )}
    </div>
  )
}

type AddBlockType = {
  data: {
    parentId: string
    updateGraph: (data: UpdateGraphParams) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema?: any
    disabled?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik?: any
  }
  id: string
}

/**
 * Add block node, responsible for adding nodes when creating/editing a workflow.
 * @param {void} updateGraph updateGraph function
 * @param {string} id add block node id
 * @param {string} parentId parent id for add block node
 * @returns {React.ReactElement}
 */

/**
 * AddBlockNode component that provides a dropdown menu for adding different types of nodes to a workflow.
 * Supports various node types including calculators, scripts, filters, loops, and selection nodes.
 */
export const AddBlockNode = memo(function AddBlockNode(props: Readonly<AddBlockType>): React.ReactElement {
  // Prop destructuring
  const updateGraph = props.data?.updateGraph
  const { id } = props

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // React Flow hooks
  const store = useStoreApi()
  const { getNodes } = store.getState()
  const { getEdges } = useReactFlow()

  // Theme hook
  const theme = getTheme()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "addBlockNode",
  })

  // Constants
  const nodes = getNodes()
  const currentIndex = nodes?.findIndex((node: AddBlockType) => node?.id === id)
  const page = 1
  const pageSize = 1

  // Data fetching queries
  const { data: rulesets, isLoading: isRulesetsLoading } = useQuery<
    AxiosResponse<PaginatedRuleSetListList>,
    AxiosError
  >(["rulesets", projectId, page, pageSize], () => KonanAPI.fetchRulesets(projectId as string, page, pageSize), {
    enabled: !!projectId,
  })

  const { isLoading: isTaglistsLoading, data: taglists } = useQuery<
    AxiosResponse<PaginatedTagListListList>,
    AxiosError
  >(["taglists", projectId, page, pageSize], () => KonanAPI.fetchTagLists(projectId as string, page, pageSize), {
    enabled: !!projectId,
  })

  const { isLoading: isScorecardsetsLoading, data: scorecardsets } = useQuery<
    AxiosResponse<PaginatedScoreCardSetListList>,
    AxiosError
  >(
    ["scorecardsets", projectId, page, pageSize],
    () => KonanAPI.fetchScorecardsets({ projectId: projectId as string, page, pageSize }),
    {
      enabled: !!projectId,
    },
  )

  const { isLoading: isProgramsLoading, data: programs } = useQuery<AxiosResponse<PaginatedProgramList>, AxiosError>(
    ["programs", projectId, page, pageSize],
    () => KonanAPI.fetchPrograms(projectId as string, page, pageSize),
    {
      enabled: !!projectId,
    },
  )

  const { isLoading: isCalculatorsLoading, data: calculators } = useQuery<
    AxiosResponse<PaginatedCalculatorListList>,
    AxiosError
  >(
    ["calculators", projectId, page, pageSize],
    () => KonanAPI.listCalculators({ projectId: projectId as string, page, page_size: pageSize }),
    {
      enabled: !!projectId,
    },
  )

  const { data: scripts, isLoading: isScriptsLoading } = useQuery<
    AxiosResponse<PaginatedScriptGroupListList>,
    AxiosError
  >(["scripts", projectId, page, pageSize], () => KonanAPI.fetchScripts(projectId as string, page, pageSize), {
    enabled: !!projectId,
  })

  const { isLoading: isProjectsLoading, data: projects } = useQuery<
    AxiosResponse<PaginatedDeploymentListList>,
    AxiosError
  >(
    ["projects", page, pageSize],
    () =>
      KonanAPI.fetchProjects({
        page,
        pageSize,
        category: "model",
        type: ["classification", "credit_scoring"],
      }),
    {
      enabled: !!projectId,
    },
  )

  // Memoized values
  const genericRulesetsMemo = useMemo(() => {
    const generics = rulesets?.data?.results?.filter((ruleset) => ruleset?.type === RuleSetCreateRequest.type.GENERIC)
    return generics
  }, [rulesets?.data?.results])

  const currentPid = useMemo(() => {
    const currentNode = nodes?.find((node) => node?.id === id)
    return currentNode?.data?.parentId
  }, [id, nodes])

  // Helper functions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getNodeData = (nodeType: string): any => {
    switch (nodeType) {
      case "RulesetNode":
      case "DecisionNode":
        return rulesets?.data
      case "ProgramNode":
        return programs?.data
      case "ScorecardsetNode":
        return scorecardsets?.data
      case "ProjectNode":
        return projects?.data
      case "ScriptNode":
        return scripts?.data
      default:
        return {}
    }
  }

  /**
   * Handles block selection from the add block menu.
   * Determines routing logic and creates appropriate nodes based on the selected type.
   * Examples: ruleset node -> 2 nodes, filter node -> 3 nodes (with true/false routes).
   */
  const onSelectingBlock = useCallback(
    (type: string, optionalProps?: Record<string, unknown>): void => {
      const parentId = currentIndex === 1 ? "1" : (currentPid ?? "")
      const isSelectedNodeInFilter = isNodeInFilter(parentId, nodes)
      const isSelectedNodeInLoop = isNodeInLoop(parentId, nodes)

      const currentEdge = getEdges()?.find((edge) => edge.target === id)

      const newNodes = preDefinedRoutesForNodes(
        {
          nodeType: type,
          newNodeId: id,
          parentId,
          updateGraph,
          formik: props?.data?.formik,
          schema: props?.data?.schema,
          filterBranch: ["true", "false"].includes(currentEdge?.data?.text)
            ? currentEdge?.data?.text
            : (isSelectedNodeInFilter.filterBranch as string),
          isNodeInFilter: isSelectedNodeInFilter.isNodeInFilter,
          filterId: isSelectedNodeInFilter.filterId,
          ...isSelectedNodeInLoop,
          ...optionalProps,
        },
        getNodeData(type),
      )
      if (props?.data?.updateGraph) {
        props?.data?.updateGraph({ arrayOfNewNodes: newNodes, isNodeBetween: false })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPid],
  )

  return (
    <Grid container item xs={12}>
      <Handle
        style={{ opacity: 0 }}
        className="custom-handle"
        type="target"
        position={Position.Top}
        id="b"
        isConnectable={true}
      />
      <FormControl variant="standard" size="small">
        <Select
          variant="standard"
          labelId="modelUUID"
          id="selectModelUUID"
          value={"default"}
          disabled={props?.data?.disabled}
          disableUnderline
          displayEmpty
          className={`sort nodrag`}
          MenuProps={{
            PaperProps: {
              style: {
                backgroundColor: theme.palette.base.background[1],
                border: theme.palette.base.background.enabled,
              },
            },
            classes: {
              paper: styles.menu,
            },
            variant: "selectedMenu",
          }}
        >
          <MenuItem key="none" value="default" disabled style={{ display: "none" }}>
            <Typography variant="label-bold" style={{ paddingTop: "6px" }}>
              {t("addBlock")}
            </Typography>
          </MenuItem>

          {/* Ruleset node */}
          <MenuItemWithTooltip
            Item={<RulesetNode menuView={true} data={{ nodeType: "RulesetNode" }} />}
            onSelectingBlock={() => onSelectingBlock("RulesetNode")}
            tooltipCondition={!isRulesetsLoading && genericRulesetsMemo?.length === 0}
            tooltipText={t("menuTooltips.rulesetNode")}
            isNodeDisabled={isRulesetsLoading}
          />

          <Box mt={1} />
          <MenuItem onClick={() => onSelectingBlock("DecisionNode")}>
            <RulesetNode menuView={true} data={{ nodeType: "DecisionNode" }} />
          </MenuItem>

          <Box mt={1} />
          {/* Taglist node */}
          <MenuItemWithTooltip
            Item={<TaglistNode menuView={true} data={{ nodeType: "TaglistNode" }} />}
            onSelectingBlock={() => onSelectingBlock("TaglistNode")}
            tooltipCondition={!isTaglistsLoading && taglists?.data?.results?.length === 0}
            tooltipText={t("menuTooltips.taglistNode")}
            isNodeDisabled={isTaglistsLoading}
          />

          <Box mt={1} />
          {/* Scorecardset node */}
          <MenuItemWithTooltip
            Item={<ScorecardsetNode menuView={true} data={{ nodeType: "ScorecardsetNode" }} />}
            onSelectingBlock={() => onSelectingBlock("ScorecardsetNode")}
            tooltipCondition={!isScorecardsetsLoading && scorecardsets?.data?.results?.length === 0}
            tooltipText={t("menuTooltips.scorecardsetNode")}
            isNodeDisabled={isScorecardsetsLoading}
          />
          <Box mt={1} />

          {/* Program node */}
          <MenuItemWithTooltip
            Item={<ProgramNode menuView={true} data={{ nodeType: "ProgramNode" }} />}
            onSelectingBlock={() => onSelectingBlock("ProgramNode")}
            tooltipCondition={!isProgramsLoading && programs?.data?.results?.length === 0}
            tooltipText={t("menuTooltips.programNode")}
            isNodeDisabled={isProgramsLoading}
          />

          <Box mt={1} />
          <MenuItem onClick={() => onSelectingBlock("FilterNode")}>
            <FilterNode menuView={true} />
          </MenuItem>

          <Box mt={1} />
          <MenuItemWithTooltip
            Item={<CalculatorNode menuView data={{ nodeType: "CalculatorNode" }} />}
            onSelectingBlock={() => onSelectingBlock("CalculatorNode")}
            tooltipCondition={!isCalculatorsLoading && calculators?.data?.results?.length === 0}
            tooltipText={t("menuTooltips.calculatorNode")}
            isNodeDisabled={isCalculatorsLoading}
          />

          {isEnvVariableTruthy(String(getEnvVar("WORKFLOW_LOOPS_ENABLED"))) && (
            <Fragment>
              <Box mt={1} />
              <MenuItem onClick={() => onSelectingBlock("LoopStartNode")}>
                <LoopStartNode menuView={true} />
              </MenuItem>
            </Fragment>
          )}

          <Box mt={1} />
          {/* Script node */}
          <MenuItemWithTooltip
            Item={<ScriptNode menuView={true} data={{ nodeType: "ScriptNode" }} />}
            onSelectingBlock={() => onSelectingBlock("ScriptNode")}
            tooltipCondition={!isScriptsLoading && scripts?.data?.results?.length === 0}
            tooltipText={t("menuTooltips.scriptNode")}
            isNodeDisabled={isScriptsLoading}
          />

          <MenuItemWithTooltip
            Item={<ScriptNode menuView={true} data={{ nodeType: "ScriptNode", scriptType: "marketplace" }} />}
            onSelectingBlock={() => onSelectingBlock("ScriptNode", { scriptType: "marketplace" })}
            tooltipCondition={!isScriptsLoading && scripts?.data?.results?.length === 0}
            tooltipText={
              "This project doesn't have any integrations, therefore you can't add an integration node in a workflow"
            }
            isNodeDisabled={isScriptsLoading}
          />

          <Box mt={1} />
          {/* Project node */}
          <MenuItemWithTooltip
            Item={<ProjectNode menuView={true} data={{ nodeType: "ProjectNode" }} />}
            onSelectingBlock={() => onSelectingBlock("ProjectNode")}
            tooltipCondition={!isProjectsLoading && projects?.data?.count === 0}
            tooltipText={t("menuTooltips.projectNode")}
            isNodeDisabled={isProjectsLoading}
          />

          <Box mt={1} />
          <MenuItem onClick={() => onSelectingBlock("EndResultNode")}>
            <LabeledNode data={{ nodeType: "EndResultNode" }} menuView={true} />
          </MenuItem>
        </Select>
      </FormControl>
    </Grid>
  )
})
